// Compliance-aware terminology and comprehensive FAQ for OFinancial Markets.

// OFinancial Markets is strictly MT5-only. No other platform offerings.
export const PLATFORMS = [
  { id: "mt5", name: "MetaTrader 5", logo: "/brand/platforms/MT5.png", accent: "#54d97c", accentDeep: "#2e9b50" },
];

// OFinancial account types (replaces challenges). 4 tiers + Social variants.
// Min deposit, spreads, commission, leverage and key specs sourced from
// the live ofinancial.markets account-types section.
export const ACCOUNTS = {
  classic: {
    label: "Classic",
    blurb: "Ideal for traders seeking classic conditions with competitive spreads and flexible leverage.",
    minDeposit: "$50",
    spreadFrom: "1.0",
    commission: "$0",
    leverage: "1:1000",
    baseCurrencies: ["EUR", "USD", "GBP"],
    execution: "Market Execution",
    minLot: "0.01",
    maxLot: 100,
    badge: null,
  },
  premier: {
    label: "Premier",
    blurb: "Tailored for seasoned traders looking for tighter spreads and priority customer support.",
    minDeposit: "$1,000",
    spreadFrom: "0.0",
    commission: "$3.00",
    leverage: "1:1000",
    baseCurrencies: ["EUR", "USD", "GBP"],
    execution: "Market Execution",
    minLot: "0.01",
    maxLot: 200,
    badge: "Most Popular",
  },
  elite: {
    label: "Elite",
    blurb: "For advanced or institutional investors, with ultra-low spreads and personalised account management.",
    minDeposit: "$5,000",
    spreadFrom: "0.0",
    commission: "$2.50",
    leverage: "1:500",
    baseCurrencies: ["EUR", "USD", "GBP"],
    execution: "Market Execution",
    minLot: "0.01",
    maxLot: 500,
    badge: "Premium",
  },
  social: {
    label: "Social",
    blurb: "Copy-trade from top performers or share your own strategy · built on top of our Classic account conditions, built into your Trader's Room.",
    minDeposit: "$50",
    spreadFrom: "1.0",
    commission: "$0",
    leverage: "1:500",
    baseCurrencies: ["EUR", "USD", "GBP"],
    execution: "Market Execution",
    minLot: "0.01",
    maxLot: 50,
    badge: "Copy-trading",
  },
  cent: {
    label: "Cent",
    blurb: "Trade in cents (USC) with micro-sized risk · the ideal starter account to learn live markets with minimal capital.",
    minDeposit: "$50",
    spreadFrom: "1.0",
    commission: "$0",
    leverage: "1:500",
    baseCurrencies: ["USC"],
    execution: "STP · No-Dealing-Desk",
    minLot: "0.01",
    maxLot: 100,
    badge: null,
    bonusEligible: false,
  },
};

// Social media profile URLs.
export const SOCIAL_LINKS = {
  facebook:  "https://www.facebook.com/OFinancial.Global",
  youtube:   "https://www.youtube.com/channel/OFinancialMarkets",
  twitter:   "https://twitter.com/OFinancialMarkets/",
  instagram: "https://www.instagram.com/ofinancial.markets/",
  linkedin:  "https://www.linkedin.com/company/OFinancialMarkets",
};

// Default destination for every "Open Account" / "Register" CTA across
// the site. Points straight at the CRM's hosted signup page.
//
// EXCEPTION · the *header* "Open Account" button can override this and
// open the in-app multi-step wizard popup instead, but ONLY when the
// admin has flipped `signup_wizard_enabled` in the dashboard. See the
// gating logic in <Navbar /> + <SignupModalProvider />.
export const REGISTER_URL = "https://live.ofinancial.markets/signup";

// Sentinel href · the header's "Open Account" button uses this when
// the in-app signup-wizard modal is enabled. <SignupModalProvider>
// listens for clicks on `a[href="#open-signup"]` and pops the wizard
// instead of navigating. Never put this anywhere else.
export const SIGNUP_MODAL_HREF = "#open-signup";
export const SIGNIN_URL   = "https://live.ofinancial.markets/login";
export const WEBTRADER_URL = "https://webtrader.ofinancial.markets/terminal";
export const BUY_BASE = "https://live.ofinancial.markets/signup";

// Official MT5 download endpoints · sourced from ofinancial.markets/mt5
export const MT5_DOWNLOADS = {
  windows: "https://download.terminal.free/cdn/web/omni.markets.llc/mt5/omnimarkets5setup.exe",
  mac:     "https://download.terminal.free/cdn/web/omni.markets.llc/mt5/omnimarkets5setup.exe",
  android: "https://download.terminal.free/cdn/mobile/mt5/android?server=OmniMarkets-OFinancial",
  ios:     "https://download.terminal.free/cdn/mobile/mt5/ios?server=OmniMarkets-OFinancial",
  web:     "https://webtrader.ofinancial.markets/terminal",
  webTerminal: "https://webtrader.ofinancial.markets/terminal", // alias used by older callers
};

// Real planID mappings · sourced from official OFinancial Markets plan-links sheet.
// Key format: `${program}_${sizeKey}_${platform}` where sizeKey is "5K","10K","25K","50K","100K","250K"
export const PLAN_IDS = {
  // MT5 plan IDs · all other platforms removed (MT5-only broker).
};

export function sizeKey(sizeLabel) {
  // "$10,000" -> "10K", "$250,000" -> "250K", "$5,000" -> "5K"
  const n = Number(sizeLabel.replace(/[$,]/g, ""));
  if (n >= 1000) return `${Math.round(n / 1000)}K`;
  return `${n}`;
}

export function buyUrl(program, sizeLabel, platform) {
  const key = `${program}_${sizeKey(sizeLabel)}_${platform}`;
  const planId = PLAN_IDS[key];
  return planId ? `${BUY_BASE}?planID=${planId}` : BUY_BASE;
}

// OFinancial broker FAQ · authored for the broker context.
export const FAQ_CATEGORIES = [
  {
    id: "general",
    label: "General",
    items: [
      { id: "general_1", q: "What is OFinancial Markets?", a: "OFinancial Markets is an online broker offering FX, metals, indices, energies, share CFDs and crypto across one institutional MT5 platform. We are operated by OMNI Markets LLC · part of a financial group authorized and licensed in Antigua and Mauritius." },
      { id: "general_2", q: "What trading platform do you offer?", a: "MetaTrader 5 (MT5) · the industry-standard platform. Available as a Windows / macOS desktop installer, web terminal (no install required), and iOS / Android mobile apps. MQL5 expert advisors are fully supported." },
      { id: "general_3", q: "What can I trade?", a: "Over 1200 instruments: 60+ FX pairs (majors, minors, exotics), spot gold / silver / platinum / palladium, global indices (US30, NAS100, SPX500, DAX, FTSE, NIK225 etc.), WTI & Brent oil, natural gas, 750+ shares CFDs, and 70+ crypto CFDs (BTC, ETH and major altcoins)." },
      { id: "general_4", q: "What leverage do you offer?", a: "Dynamic volume-tiered leverage up to 1:1000, tiered per instrument: on gold (XAUUSD) 1:1000 up to 0.20 lots of total open volume, on FX majors 1:1000 up to 1 lot — stepping down to 1:500, 1:200 and 1:100 as open volume grows. Indices and oil up to 1:100, crypto 1:20, share CFDs 1:10." },
      { id: "general_5", q: "Which countries do you accept clients from?", a: "We accept clients from most jurisdictions, subject to local laws. We do not offer services to residents of the USA, Iraq, Myanmar, North Korea or OFAC-listed countries. Other country-specific restrictions may apply at our discretion." },
      { id: "general_6", q: "What is the minimum age to open an account?", a: "You must be at least 18 years old, or the applicable legal age in your country of residence, to open an account with OFinancial." },
      { id: "general_7", q: "Are my funds safe?", a: "Yes. Client funds are held in fully segregated accounts at tier-1 banks, separate from the company's operating capital. Retail clients also benefit from negative balance protection." },
      { id: "general_8", q: "What are your trading hours?", a: "Each instrument has its own session schedule. FX trades 24/5 (Sun 22:00 GMT to Fri 22:00 GMT). Metals trade nearly 24/5 with short daily breaks. Indices align to their underlying exchange hours. Crypto trades 24/7. Right-click any symbol in MT5 to view exact hours." },
    ],
  },
  {
    id: "accounts",
    label: "Accounts",
    items: [
      { id: "accounts_1", q: "What account types do you offer?", a: "Five accounts: Classic ($50 min, 1.0 pip spreads, no commission), Premier ($1,000 min, raw 0.0 pip spreads, $3.00 per side commission), Elite ($5,000 min, raw 0.0 pip spreads, $2.50 per side commission), Social (copy-trade from top providers, no commission), and Cent ($50 min, 1.0 pip spreads, no commission, USC micro-lot account for new traders). Islamic swap-free conditions are available on Classic and Cent only · FX and Gold for up to two weeks per position." },
      { id: "accounts_2", q: "How long does account opening take?", a: "Most accounts are approved the same business day. After signup you complete KYC by uploading a government-issued ID and proof of address · once verified, your live account is ready to fund." },
      { id: "accounts_3", q: "Can I have multiple accounts?", a: "Yes. You can hold multiple live accounts (e.g. one Premier for FX, one Elite for indices, one swap-free for Islamic compliance) all under the same client profile." },
      { id: "accounts_4", q: "Do you offer demo accounts?", a: "Yes. Demo accounts are available with virtual funds and live market data so you can test your strategy or familiarise yourself with MT5 before depositing real capital." },
      { id: "accounts_5", q: "Can I switch between account types?", a: "Existing accounts cannot be converted, but you can open a new account of a different type and transfer balance internally between them at any time." },
    ],
  },
  {
    id: "bonus",
    label: "Bonus",
    items: [
      { id: "bonus_1", q: "What is the First Deposit Bonus?", a: "New clients can claim a 50% bonus on their first deposit, up to a maximum of USD 5,000. The bonus increases your trading capital · profits earned through trading are fully withdrawable." },
      { id: "bonus_2", q: "What is the minimum deposit for the bonus?", a: "USD/EUR 100. You can deposit more (up to USD 10,000 for the max bonus of $5,000) but you must deposit at least 100 to qualify." },
      { id: "bonus_3", q: "Which accounts are eligible for the bonus?", a: "The 50% First Deposit Bonus is available on any eligible live trading account. It is not applicable to PAMM, investment wallets, or Social Trading accounts." },
      { id: "bonus_4", q: "Are there country restrictions on the bonus?", a: "Yes. For clients from Nigeria, Malaysia and Indonesia the maximum bonus is 20%. The offer is not available to clients from India, Pakistan, the UAE, Bangladesh, Egypt and Vietnam. Existing clients should contact support@ofinancial.markets for personalised offers." },
      { id: "bonus_5", q: "Can I withdraw the bonus?", a: "No · the bonus itself is non-withdrawable and serves only as trading capital. Any profits earned while trading with the bonus are 100% yours and fully withdrawable; the bonus is removed when you request a withdrawal." },
      { id: "bonus_6", q: "How long does it take to receive the bonus?", a: "We process bonus requests within 24 working hours after your deposit clears. The bonus appears in the Credit field of your MT5 platform." },
    ],
  },
  {
    id: "funding",
    label: "Funding & Withdrawals",
    items: [
      { id: "funding_1", q: "How can I fund my account?", a: "We accept cards (Visa / Mastercard), e-wallets (Skrill, Neteller, FasaPay), crypto (USDT TRC20/ERC20, BTC, ETH), and bank wire. Most deposits are instant; bank wires take 1–3 business days." },
      { id: "funding_2", q: "Are there deposit fees?", a: "OFinancial does not charge any deposit fees. Your payment provider may apply their own fees, which are disclosed up-front before you confirm the deposit." },
      { id: "funding_3", q: "How long do withdrawals take?", a: "Withdrawals are processed within 24 hours on business days. The funds typically reach you within 1–3 business days depending on the method." },
      { id: "funding_4", q: "What is the minimum withdrawal?", a: "USD/EUR 10 for e-wallets and crypto. USD/EUR 100 for bank wire withdrawals." },
      { id: "funding_5", q: "Are there withdrawal fees?", a: "Some withdrawal methods carry a processor fee (shown on each method's card on the Funding page). Any applicable fee is disclosed up-front before you confirm the withdrawal." },
      { id: "funding_6", q: "Can I withdraw to a different method than I deposited from?", a: "Per AML regulations, we return funds via the same channel you deposited from, up to the deposit amount. Profits beyond the deposit amount can be withdrawn via any verified method." },
    ],
  },
  {
    id: "platform",
    label: "MT5 Platform",
    items: [
      { id: "platform_1", q: "How do I download MT5?", a: "Download links for the Windows desktop installer, macOS package, and iOS / Android mobile apps are available on our Platforms page. Or use the MT5 Web Terminal directly in your browser · no installation needed." },
      { id: "platform_2", q: "Can I use Expert Advisors (EAs)?", a: "Yes. MT5 fully supports MQL5 Expert Advisors. Eligible accounts also receive free VPS hosting for 24/7 EA uptime." },
      { id: "platform_3", q: "Is hedging allowed?", a: "Yes. Hedging is permitted across all our account types · you can hold long and short positions in the same symbol simultaneously." },
      { id: "platform_4", q: "Do you offer one-click trading?", a: "Yes. MT5's one-click trading mode is supported across all account types and devices." },
      { id: "platform_5", q: "What is your typical execution speed?", a: "Sub-millisecond order execution from our London LD4 colocated infrastructure to tier-1 liquidity providers. Direct market access." },
    ],
  },
  {
    id: "support",
    label: "Support",
    items: [
      { id: "support_1", q: "How do I contact support?", a: "Our team is available 24 hours, 5 days a week via live chat (bottom-right bubble), email at support@ofinancial.markets, or phone at +357 25 000 000." },
      { id: "support_2", q: "What's your response time?", a: "Live chat responses are typically under 2 minutes during market hours. Email queries are answered within 1 business day." },
      { id: "support_3", q: "Do you offer trading education?", a: "Yes. Our Glossary covers every key trading term, and our FAQ + Help Centre answer the most common questions. Trading-specific lessons are coming soon · in the meantime our calculators, swap-rate tables and live spreads page let you learn by doing." },
    ],
  },
];

export const TICKER_SYMBOLS = [
  { symbol: "EURUSD", price: 1.04655, change: -0.12 },
  { symbol: "GBPUSD", price: 1.25853, change: -0.05 },
  { symbol: "XAUUSD", price: 2666.40, change: -1.94 },
  { symbol: "XAGUSD", price: 30.720, change: 2.14 },
  { symbol: "GBPJPY", price: 194.019, change: -0.05 },
  { symbol: "GER30",  price: 19398.4, change: -0.20 },
  { symbol: "BTCUSD", price: 67421.5, change: 1.42 },
  { symbol: "ETHUSD", price: 3284.16, change: 0.88 },
  { symbol: "USDJPY", price: 156.82, change: 0.21 },
  { symbol: "USOIL",  price: 71.34, change: -0.65 },
  { symbol: "NAS100", price: 21034.5, change: 0.42 },
  { symbol: "SPX500", price: 6051.2, change: 0.18 },
];

